import { useCallback, useEffect, useState } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import { ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { horaCurta, lerInsightsDoAdmin } from '../../paineis';
import type { InsightsDoAdmin, TomDoInsight } from '../../paineis';
import { CartaoInsight } from './CartoesDeInsight';
import { SubnavDoPainel } from './SubnavDoPainel';
import { trocarConsulta } from './pecasDoPainel';
import './insights.css';

/**
 * TELA 12 C — INSIGHTS (admin): "o que está acontecendo na operação e onde preciso agir?"
 *
 * No topo o Resumo Inteligente (as frases saem dos cartões, com os números). Embaixo, os cartões
 * por área — vendas, premiação e programas, equipe, pré-vencidos, auditoria, dados e sistema —
 * cada um com o número, a conta e o botão que leva direto a agir. Só dado real: o que não tem
 * dado não aparece (não preenchemos a tela para parecer cheia).
 */

type Filtro = 'todos' | TomDoInsight;
const FILTROS: readonly [Filtro, string][] = [
  ['todos', 'Todos'],
  ['alerta', 'Atenção'],
  ['oportunidade', 'Oportunidades'],
  ['positivo', 'Bons sinais'],
  ['neutro', 'Informações'],
];
const COR: Record<TomDoInsight, string> = {
  alerta: '#e04a57',
  oportunidade: '#e0a33a',
  positivo: '#39b37f',
  neutro: '#6aa6e0',
};

type Estado =
  | { fase: 'carregando' }
  | { fase: 'erro' }
  | { fase: 'sem-acesso' }
  | { fase: 'pronto'; d: InsightsDoAdmin };

export function Insights({ eu, parametros }: { eu: Eu | null; parametros: URLSearchParams }) {
  const f = parametros.get('f') as Filtro | null;
  const filtro: Filtro = FILTROS.some(([x]) => x === f) ? (f as Filtro) : 'todos';
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });

  const carregar = useCallback(() => {
    let vivo = true;
    setEstado({ fase: 'carregando' });
    void lerInsightsDoAdmin().then((r) => {
      if (!vivo) return;
      if (r.ok) setEstado({ fase: 'pronto', d: r.dados });
      else setEstado({ fase: r.falha.tipo === 'sem-acesso' ? 'sem-acesso' : 'erro' });
    });
    return () => {
      vivo = false;
    };
  }, []);
  useEffect(carregar, [carregar]);

  if ((eu && !podeFazer(eu, 'painel.ver_completo')) || estado.fase === 'sem-acesso')
    return <SemAcesso area="Insights" />;

  const d = estado.fase === 'pronto' ? estado.d : null;
  const todos = d ? d.grupos.flatMap((g) => g.cartoes) : [];
  const conta = (t: TomDoInsight) => todos.filter((c) => c.tom === t).length;
  const acao = conta('alerta') + conta('oportunidade');

  return (
    <div className="is">
      <SubnavDoPainel atual="insights" insights={d ? acao : undefined} />
      <div className="is-corpo">
        <div className="is-topo">
          <div className="is-titulo">
            <h1>Insights</h1>
            <p>
              {d
                ? `${String(todos.length)} cartões · ${String(acao)} pedem ação · calculado às ${horaCurta(d.calculadoEm)} sobre vendas do HOS, lançamentos, cheque, Giro e API`
                : 'Calculando…'}
            </p>
          </div>
          <button
            type="button"
            className="is-botao"
            onClick={carregar}
            disabled={estado.fase === 'carregando'}
          >
            <Icone nome="retry" tamanho={14} traco={1.7} />
            Atualizar
          </button>
        </div>

        {estado.fase === 'erro' && <ErroDeArea oQue="os insights" aoTentar={carregar} />}
        {estado.fase === 'carregando' && (
          <div className="in-vazio">Calculando os insights da operação…</div>
        )}

        {d && (
          <>
            <section className="in-resumo" aria-label="Resumo inteligente">
              <div className="in-resumo-rotulo">Resumo desta semana</div>
              <p>
                {d.resumo.length
                  ? d.resumo.join(' ')
                  : 'Sem movimento suficiente para resumir: ainda não há venda nem lançamento no período.'}
              </p>
              <div className="in-resumo-contagem">
                {(['alerta', 'oportunidade', 'positivo', 'neutro'] as const).map((t) =>
                  conta(t) ? (
                    <span key={t}>
                      <i style={{ background: COR[t] }} />
                      {conta(t)} {FILTROS.find(([x]) => x === t)?.[1].toLowerCase()}
                    </span>
                  ) : null,
                )}
              </div>
            </section>

            <div className="in-filtros" role="tablist" aria-label="Filtrar cartões">
              {FILTROS.map(([id, rotulo]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={filtro === id}
                  className={`in-chip${filtro === id ? ' in-chip-ativo' : ''}`}
                  onClick={() => trocarConsulta(parametros, { f: id === 'todos' ? null : id })}
                >
                  {rotulo}
                  {id !== 'todos' && conta(id) ? ` · ${String(conta(id))}` : ''}
                </button>
              ))}
            </div>

            {d.grupos.map((g) => {
              const cartoes =
                filtro === 'todos' ? g.cartoes : g.cartoes.filter((c) => c.tom === filtro);
              if (!cartoes.length) return null;
              return (
                <section key={g.id} className="in-grupo">
                  <h2>{g.titulo}</h2>
                  <div className="in-grade">
                    {cartoes.map((c) => (
                      <CartaoInsight key={c.id} c={c} />
                    ))}
                  </div>
                </section>
              );
            })}
            {filtro !== 'todos' && conta(filtro) === 0 && (
              <div className="in-vazio">Nenhum cartão deste tipo agora.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
